import { useEffect, useState } from "react";
import { getSettings } from "@/lib/api";
import { PlayCircle } from "lucide-react";

/**
 * Convert a YouTube/Vimeo URL to its embed equivalent.
 * Returns null if the URL is not a known platform (caller falls back to <video>).
 */
function toEmbedUrl(raw) {
    if (!raw) return null;
    try {
        const url = new URL(raw);
        const host = url.hostname.replace(/^www\./, "");

        // YouTube
        if (host === "youtube.com" || host === "m.youtube.com") {
            const id = url.searchParams.get("v");
            if (id) return `https://www.youtube.com/embed/${id}`;
            // /embed/<id> or /shorts/<id>
            const parts = url.pathname.split("/").filter(Boolean);
            if (parts[0] === "embed" || parts[0] === "shorts")
                return `https://www.youtube.com/embed/${parts[1]}`;
        }
        if (host === "youtu.be") {
            const id = url.pathname.split("/").filter(Boolean)[0];
            if (id) return `https://www.youtube.com/embed/${id}`;
        }

        // Vimeo
        if (host === "vimeo.com") {
            const id = url.pathname.split("/").filter(Boolean)[0];
            if (id && /^\d+$/.test(id)) return `https://player.vimeo.com/video/${id}`;
        }
    } catch {
        /* swallow */
    }
    return null;
}

function isDirectVideo(url) {
    return /\.(mp4|webm|ogg|mpg|mpeg|mov)(\?|$)/i.test(url || "");
}

export default function VideoEmbedBlock() {
    const [settings, setSettings] = useState(null);

    useEffect(() => {
        getSettings()
            .then(setSettings)
            .catch(() => setSettings({}));
    }, []);

    const url = settings?.hero_video_url || "";
    const title = settings?.hero_video_title || "";
    const caption = settings?.hero_video_caption || "";
    const embed = toEmbedUrl(url);
    const direct = !embed && isDirectVideo(url);

    return (
        <section
            className="mt-16 px-4 md:px-8 lg:px-12"
            data-testid="video-embed-section"
        >
            {(title || caption) && (
                <header className="mb-5">
                    {title && (
                        <h2 className="section-title" data-testid="video-title">
                            {title}
                        </h2>
                    )}
                    {caption && (
                        <p className="font-sofia text-[#1B1B1B]/75 mt-1 max-w-2xl">
                            {caption}
                        </p>
                    )}
                </header>
            )}
            <div
                className="relative w-full bg-[#1B1B1B] overflow-hidden"
                style={{ aspectRatio: "3 / 2" }}
                data-testid="video-embed-block"
            >
                {embed ? (
                    <iframe
                        src={embed}
                        title={title || "Embedded video"}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        allowFullScreen
                        className="absolute inset-0 w-full h-full"
                        data-testid="video-iframe"
                    />
                ) : direct ? (
                    <video
                        src={url}
                        controls
                        preload="metadata"
                        className="absolute inset-0 w-full h-full object-cover"
                        data-testid="video-element"
                    />
                ) : (
                    <div
                        className="absolute inset-0 flex flex-col items-center justify-center text-white/80 gap-3"
                        data-testid="video-placeholder"
                    >
                        <PlayCircle className="h-16 w-16 text-[#F5C53D]" />
                        <p className="font-poppins tracking-[0.18em] uppercase text-xs">
                            Video coming soon
                        </p>
                        <p className="font-sofia text-sm text-white/60">
                            Add a YouTube / Vimeo / MP4 URL from{" "}
                            <span className="text-[#F5C53D]">/admin → Settings</span>
                        </p>
                    </div>
                )}
            </div>
        </section>
    );
}
